const DEFAULT_ORIGIN = 'https://app.biel.ai';

/**
 * Strips a trailing `/api/v1` or `/api/v2` (and trailing slashes) so the
 * `api-url` attribute accepts either a bare origin or a versioned base URL.
 */
function apiOrigin(baseUrl: string): string {
  return baseUrl.replace(/\/api\/v[12]\/?$/, '').replace(/\/+$/, '');
}

export class BielService {
  private project: string;
  private apiKey: string;
  private errorMessage404: string;
  private errorMessage403: string;
  private errorMessageDefault: string;
  private baseUrl: string;

  constructor(
    project: string,
    errorMessage404: string,
    errorMessage403: string,
    errorMessageDefault: string,
    apiKey: string = '',
    baseUrl: string = ''
  ) {
    this.project = project;
    this.apiKey = apiKey;
    this.errorMessage404 = errorMessage404;
    this.errorMessage403 = errorMessage403;
    this.errorMessageDefault = errorMessageDefault;
    const origin = apiOrigin(baseUrl || DEFAULT_ORIGIN);
    this.baseUrl = `${origin}/api/v2`;
  }

  private getHeaders(includeContentType: boolean = true): HeadersInit {
    const headers: HeadersInit = {};

    if (includeContentType) {
      headers['Content-Type'] = 'application/json; charset=UTF-8';
    }

    if (this.apiKey) {
      headers['Authorization'] = `Api-Key ${this.apiKey}`;
    }

    return headers;
  }

  async getProjectDetails(): Promise<
    | {
        whitelabel: boolean;
        suggestedQuestions: string[];
        initialMessage: string;
        filters: { title: string }[];
        think_mode: boolean;
        mcp_enabled: boolean;
        mcp_server_name: string;
        mcp_metadata_tag: string;
        actions: { name: string; button_text: string; url: string }[];
      }
    | string
  > {
    try {
      const response = await fetch(`${this.baseUrl}/projects/${this.project}/`, {
        headers: this.getHeaders(false),
      });

      if (response.status === 404) {
        return this.errorMessage404;
      }

      if (response.status === 403) {
        return this.errorMessage403;
      }

      if (!response.ok) {
        return this.errorMessageDefault;
      }

      const data = await response.json();
      // v2 groups feature settings under config.{chat,search,mcp}; flatten
      // them back to the shape the components consume.
      const chat = data.config?.chat || {};
      const search = data.config?.search || {};
      const mcp = data.config?.mcp || {};
      return {
        whitelabel: data.whitelabel,
        suggestedQuestions: chat.suggested_questions || [],
        initialMessage: chat.initial_message,
        filters: search.filters || [],
        think_mode: chat.think_mode || false,
        mcp_enabled: mcp.enabled || false,
        mcp_server_name: mcp.server_name || '',
        mcp_metadata_tag: mcp.metadata_tag || '',
        actions: chat.actions || [],
      };
    } catch (error) {
      console.error('Error fetching project details:', error);
      return this.errorMessageDefault;
    }
  }

  async sendFeedback(messageId: string, feedback: { reaction: 0 | 1 }) {
    try {
      const response = await fetch(
        `${this.baseUrl}/projects/${this.project}/messages/${messageId}/feedback/`,
        {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify(feedback),
        }
      );

      if (!response.ok) {
        console.error('Error:', response.status);
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
  }

  async sendQuery(
    q: string,
    selected_filter_title: string
  ): Promise<{ title: string; fragment: string; url: string }[]> {
    try {
      const searchUrl = new URL(`${this.baseUrl}/projects/${this.project}/search/`);
      searchUrl.searchParams.set('q', q);
      searchUrl.searchParams.set('url', window.location.href);
      if (selected_filter_title) {
        searchUrl.searchParams.set('filter_title', selected_filter_title);
      }

      const response = await fetch(searchUrl.toString(), { headers: this.getHeaders(false) });

      if (!response.ok) {
        console.error('Error:', response.status);
        return [];
      }

      const textData = await response.text();
      if (!textData) {
        return [];
      }

      const data = JSON.parse(textData);

      return data.results || [];
    } catch (error) {
      console.error('Error sending query:', error);
      return [];
    }
  }

  async searchClicked() {
    try {
      const response = await fetch(`${this.baseUrl}/projects/${this.project}/search/click/`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        console.error('Error:', response.status);
      }
    } catch (error) {
      console.error('Error sending search clicked:', error);
    }
  }

  async getConversationMessages(chatId: string): Promise<
    {
      text: string;
      sender: 'user' | 'ai';
      messageId?: string;
      feedback?: 0 | 1;
      sources?: { title: string; url: string }[];
      isPartial: boolean;
    }[]
  > {
    // Anonymous v2 history restore (1-hour window after the last message);
    // internal endpoint, not part of the documented v2 contract.
    const url = `${this.baseUrl}/chats/${chatId}`;

    try {
      const response = await fetch(url, {
        headers: this.getHeaders(false),
      });

      if (response.status === 403) {
        throw new Error('FORBIDDEN_403');
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error fetching conversation messages:', response.status, errorText);
        return [];
      }

      const data = await response.json();

      const transformedMessages =
        data.messages?.map((msg: any) => ({
          text: msg.content,
          sender: msg.message_type === 'HUMAN' ? 'user' : 'ai',
          messageId: msg.uuid,
          feedback: msg.feedback,
          sources: msg.sources || [],
          isPartial: false,
        })) || [];

      return transformedMessages;
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      if (error.message === 'FORBIDDEN_403') {
        throw error;
      }
      return [];
    }
  }
}
